import sortBy from 'lodash/sortBy';
import orderBy from 'lodash/orderBy';
import groupBy from 'lodash/groupBy';
import flatten from 'lodash/flatten';
import maxBy from 'lodash/maxBy';
import chunk from 'lodash/chunk';
import { QUESTION_TYPES } from '../constants/assignment';
import dayjs from 'dayjs';
import lodash from 'lodash';

// 2020-01-23: Oliver disabled logs because of OOM errors on clients
// import log from 'legacy-src/utility/log';

const { keys } = Object;

export const STATUS_ICON = {
  '--default': {
    icon: 'empty_circle',
    label: 'Not started',
    className: 'not-started',
  },
  '--in-progress': {
    icon: 'half_filled_circle',
    label: 'In progress',
    className: 'started',
  },
  '--submitted': {
    icon: 'filled_circle',
    label: 'Submitted',
    className: 'submitted',
  },
  '--available-to-score': {
    icon: 'filled_circle',
    label: 'Available to score',
    className: 'submitted',
  },
  '--complete': {
    icon: 'circle_check_filled',
    label: 'Scored',
    className: 'complete circle_check_filled',
  },
  not_started: {
    icon: 'empty_circle',
    label: 'Not started',
    className: 'not-started',
  },
  started: {
    icon: 'half_filled_circle',
    label: 'In progress',
    className: 'started',
  },
  submitted: {
    icon: 'filled_circle',
    label: 'Submitted',
    className: 'submitted',
  },
  mcq_scoring: {
    icon: 'filled_circle',
    label: 'Submitted',
    className: 'submitted',
  },
  ready_to_score: {
    icon: 'filled_circle',
    label: 'Submitted',
    className: 'submitted',
  },
  scoring: {
    icon: 'filled_circle',
    label: 'Score started',
    className: 'scoring',
  },
  student_scored: {
    icon: 'circle_check_filled',
    label: 'Self-Scored',
    className: 'complete circle_check_filled',
  },
  teacher_and_student_scored: {
    icon: 'circle_check_filled',
    label: 'Self-Scored',
    className: 'complete circle_check_filled',
  },
  scored: {
    icon: 'circle_check_filled',
    label: 'Scored',
    className: 'student-scored circle_check_filled',
  },
  complete: {
    icon: 'circle_check_filled',
    label: 'Scored',
    className: 'complete circle_check_filled',
  },
  no_answer_sheet: {
    icon: 'minus_in_circle',
    label: 'No answer sheet',
    className: 'no-answer-sheet',
  },
  answer_sheet_generated: {
    icon: 'empty_circle',
    label: 'Answer sheet generated',
    className: 'answer-sheet-generated',
  },
  answer_sheet_issues: {
    icon: 'half_filled_circle',
    label: 'Answer sheet issue',
    className: 'answer-sheet-issues',
  },
};

export const SPP_STATUS_ICON = {
  not_started: {
    icon: 'empty_circle',
    label: 'Not started',
    className: ' fill-current text-gray-600',
  },
  started: {
    icon: 'half_filled_circle',
    label: 'Not Submitted',
    className: ' fill-current text-pink-200',
  },
  scored: {
    icon: 'half_filled_circle',
    label: 'Not Submitted',
    className: ' fill-current text-pink-200',
  },
  submitted: {
    icon: 'circle_check_filled',
    label: 'Submitted',
    className: ' fill-current text-purple-700',
  },
};

/**
 * Get performance level by category.
 *
 * @param  {String} category Name of the category to look for.
 * @return {Object}          PERFORMANCE_LEVELS entry to be used.
 */

/**
 * Take an object of students answers, calculate their mastery
 * and then return sorted list by mastery.
 * @param {Object} list of answers, which has student object in it.
 * @param {Boolean} fallback sorting (sort by name), when categories are same
 * @return {Array}
 **/

/**
 * Created weighed stacks by student quantity,
 * stack with most students has 3 columns and increased flex property
 * @param stacks {Array}
 * @param isWeighted {Boolean}
 * @param columns {Number}
 * @param expandedFlex {Number}
 * @returns {Array}
 */
export const createWeightedStacks = (
  stacks,
  isWeighted,
  columns = 3,
  expandedFlex = 3,
) => {
  const stackWithMaxStudents = maxBy(stacks, 'students.length');

  if (stackWithMaxStudents) {
    const maxStudents = stackWithMaxStudents.students.length;

    return stacks.map((stack) => {
      const hasAbilityToExpand =
        maxStudents > columns && stack.students.length === maxStudents;

      const hasExpandedView = Boolean(isWeighted && hasAbilityToExpand);

      const chunkBy =
        maxStudents >= columns * 2
          ? Math.round(maxStudents / columns)
          : columns;

      return {
        ...stack,
        studentGroups: hasExpandedView
          ? chunk(stack.students, chunkBy)
          : [stack.students],
        flex: hasExpandedView ? expandedFlex : 1,
        hasExpandedView,
      };
    });
  }

  return stacks;
};

export const studentsSort = (
  arr,
  primarySortingAttr = 'learnosityReportScoreParsed',
  secondarySorts = ['lastName', 'firstName'],
) => {
  const grouped = groupBy(arr, primarySortingAttr);
  const sortedGroupKeys = keys(grouped).sort(
    (a, b) => parseFloat(b) - parseFloat(a),
  );

  const sortFallBack = (x) => sortBy(x, secondarySorts);

  // each group which have same score sort by firstName then by lastName
  return flatten(sortedGroupKeys.map((key) => sortFallBack(grouped[key])));
};

const studentsWithoutReport = (students, userIds) =>
  students.filter((student) => {
    const studentId = student.user_id || student.id;
    return !userIds.includes(parseInt(studentId, 10));
  });

export const studentsByScoreBracket = ({
  topLimit = 100,
  bottomLimit = 0,
  students = [],
  reportByUsers = {},
  itemId,
  isNoResults,
  userIds,
  ignoreUnanswered = false,
}) => {
  // we can specify exactly what users ids to get from the report or use all from reportByUsers.
  const userIdsFromReports = (userIds || keys(reportByUsers)).map(parseFloat);

  const studentsSubmitted = userIdsFromReports.reduce((result, userId) => {
    const reportForUser = reportByUsers[userId];

    if (!reportForUser) {
      return result;
    }

    let reportForUserPerItem =
      itemId && reportForUser.items
        ? reportForUser.items[itemId]
        : reportForUser;

    if (!reportForUserPerItem) {
      // 2020-01-23: Oliver disabled the below logging because it was causing OOM errors on UAT. This situation might
      // be created by legitimate versions of reporting created by the World History & Geography exams that are created
      // as a composite, thus creating a false positive. More investigation is necessary, but for today's release, we're
      // removing this call.
      // log.error(`Possible Learnosity data inconsistency,
      // not found report in: ${JSON.stringify(reportByUsers)}
      // for itemId ${itemId} and student ${userId}`);
      reportForUserPerItem = {};
    }

    let { max_score: maxScore, score } = reportForUserPerItem;

    // different reports has different "not scored" data, but in sense when no or sero max score
    // it means we did not set it on scoring interface and learnosity does not know how to score it.
    const isNotScored =
      (score === null && maxScore === null) ||
      (score === '0.00' && maxScore === '0.00');
    const isAwaitingScore = isNoResults && isNotScored;

    if (score === null && !ignoreUnanswered) {
      score = '0'; // for MCQ treat un-answered as incorrect
    }

    // maxScore and score are strings "5.20"
    const percent = (parseFloat(score) / parseFloat(maxScore)) * 100;
    const isScoreInsideTierValues =
      bottomLimit <= percent && topLimit + 1 > percent;

    if (isScoreInsideTierValues || isAwaitingScore) {
      const student = students.find(
        (s) =>
          s &&
          (parseInt(s.user_id, 10) || parseInt(s.id, 10)) ===
            parseInt(userId, 10),
      );
      let meta = {};

      if (!isAwaitingScore) {
        meta = {
          userId,
          learnosityReport: reportForUserPerItem,
          learnosityReportScoreParsed: parseFloat(score),
          learnosityReportMaxScore: parseFloat(maxScore),
          percentCorrect: percent,
        };
      }

      result.push({ ...student, ...meta });
    }

    return result;
  }, []);

  // for MCQ if no report from learnosity means "no results"
  // for FRQ if no report or "report with score: null, max_score: null" considered as "no results"
  // But in general if some report is missing on Learnosity it means student did not submit an assignment.
  return studentsSubmitted.concat(
    isNoResults ? studentsWithoutReport(students, userIdsFromReports) : [],
  );
};

/**
 *  returns Object of Learnosity Reports By Users
 *  {
 *    "user-id": {
 *      ...report
 *    }
 *  }
 * @param reports {Object}
 */
export const learnosityReportByUsers = (reports) => {
  let reportByUsers = {};

  if (reports && reports.length) {
    const report = reports[0];
    const activityId = report && report.data && keys(report.data)[0];

    if (activityId) {
      reportByUsers = report.data[activityId].users;
    }
  }

  return reportByUsers;
};

/**
 * check if item types
 */
export const isMCQ = (item) =>
  item.type &&
  (item.type === 'MultipleChoiceQuestion' ||
    item.type === QUESTION_TYPES.multipleChoice);
export const isFRQ = (item) =>
  item.type &&
  (item.type === 'FreeResponseQuestion' ||
    item.type === QUESTION_TYPES.freeResponse);
export const isRubric = (item) =>
  item && item.type && item.type === 'ScoringRubricCategory';

export const isSecure = (item) => item && item.isSecure;

const sortTiers = (arr) => arr.sort((a, b) => a.topLimit - b.topLimit);

/**
 * Generate stacks out from LearnosityReport and PerformanceTiers
 * see client/src/components/assessment/Results/ResultsTable/__tests__/ResultsTable.test.js
 *
 * @param totalStudents {Number}
 * @param reports {Array}
 * @param performanceTiers {Array}
 * @param students {Array}
 * @param itemId {String}
 * @param lastLabel {String}
 * @returns {Array}
 */
export const performanceTierStacks = (
  totalStudents,
  reports,
  performanceTiers,
  students = [],
  itemId,
  lastLabel = 'No Results',
  ignoreUnanswered = false,
  secondaryNameSorts = ['lastName', 'firstName'],
) => {
  const reportByUsers = learnosityReportByUsers(reports);

  let performanceTiersArray = performanceTiers;
  if (typeof performanceTiers === 'object') {
    // In the case of Pre-AP Final Exam results this was being stored in the redux state as an object. We just want
    // the values of that object.
    performanceTiersArray = Object.values(performanceTiers);
  }

  const performanceTiersWithNoResults = [
    {
      type: 'default',
      label: lastLabel,
      isNoResults: true,
      topLimit: NaN,
      bottomLimit: NaN,
    },
    ...performanceTiersArray,
  ];

  const stackData = sortTiers(performanceTiersWithNoResults);

  // grab any report to check learnosityReportMaxScore value, useful to show/hide partial credit column
  const anyReportForItem = studentsByScoreBracket({ reportByUsers, itemId });
  const isMaxScoreMoreThanOne = Boolean(
    anyReportForItem[0] && anyReportForItem[0].learnosityReportMaxScore > 1,
  );

  // create array with "stacks" to feed into bars and performance details
  const stacks = stackData.reduce((result, tier) => {
    const { type, color, isHiddenByDefault } = tier;

    // how many students per trier ? e.g. fall between scores, or no results.
    const studentsForStack = studentsSort(
      studentsByScoreBracket({
        ...tier,
        students,
        reportByUsers,
        itemId,
        ignoreUnanswered,
      }),
      'learnosityReportScoreParsed',
      secondaryNameSorts,
    );

    const shouldPushTier =
      !isHiddenByDefault || (isHiddenByDefault && isMaxScoreMoreThanOne);

    if (shouldPushTier) {
      result.push({
        ...tier,
        value: studentsForStack.length,
        students: orderBy(
          studentsForStack,
          ['learnosityReportScoreParsed', ...secondaryNameSorts],
          ['desc', 'asc', 'asc'],
        ),
        status: color || `--${type}`,
      });
    }

    return result;
  }, []);

  // We remove no results column/stack when all students have some result, e.g. 0 students with no results
  const shouldRemoveNoResultsStack = stacks[0].value === 0;

  if (shouldRemoveNoResultsStack) {
    stacks.shift();
  }

  return stacks;
};

// convert seconds to time mm:ss
export const secondsToTime = (seconds, forceShowHours = false) => {
  if (
    Number.isNaN(seconds) ||
    seconds === 0 ||
    seconds === undefined ||
    seconds === null
  ) {
    return '';
  }
  if (seconds < 3600 && !forceShowHours) {
    return dayjs().startOf('day').add(seconds, 'seconds').format('mm:ss');
  }
  return dayjs().startOf('day').add(seconds, 'seconds').format('HH:mm:ss');
};

export const averageTimeSpentInSeconds = (averageTimeSpents) =>
  lodash.isEmpty(averageTimeSpents)
    ? 0
    : averageTimeSpents.reduce((a, b) => a + b, 0) / averageTimeSpents.length;

export const getScoreTimeSpents = (learnosityReportsByItemData) => {
  let averageTimeSpents = {};
  let userTimeSpents = {};
  let userTimeSpentPerQuestion = {};

  for (
    let assessmentIndex = 0;
    assessmentIndex < learnosityReportsByItemData.length;
    assessmentIndex++
  ) {
    const assessment = learnosityReportsByItemData[assessmentIndex];
    const assignmentKeys = assessment ? Object.keys(assessment.data) : [];
    for (
      let assignmentIndex = 0;
      assignmentIndex < assignmentKeys.length;
      assignmentIndex++
    ) {
      const assignment = assessment.data[assignmentKeys[assignmentIndex]];
      const userKeys = Object.keys(assignment.users);
      for (let userIndex = 0; userIndex < userKeys.length; userIndex++) {
        const user = assignment.users[userKeys[userIndex]];
        const questionKeys = Object.keys(user.items);
        for (
          let questionIndex = 0;
          questionIndex < questionKeys.length;
          questionIndex++
        ) {
          const question = user.items[questionKeys[questionIndex]];
          if (question.time_spent) {
            if (!averageTimeSpents[questionKeys[questionIndex]]) {
              averageTimeSpents[questionKeys[questionIndex]] = [];
            }
            if (!userTimeSpents[userKeys[userIndex]]) {
              userTimeSpents[userKeys[userIndex]] = 0;
              userTimeSpentPerQuestion[userKeys[userIndex]] = {};
            }
            averageTimeSpents[questionKeys[questionIndex]].push(
              question.time_spent,
            );
            userTimeSpents[userKeys[userIndex]] += question.time_spent;
            userTimeSpentPerQuestion[userKeys[userIndex]][
              questionKeys[questionIndex]
            ] = question.time_spent;
          }
        }
      }
    }
  }
  return { averageTimeSpents, userTimeSpents, userTimeSpentPerQuestion };
};

export const getSingleResult = (userIds, itemId, report) => {
  const reportByUsers = learnosityReportByUsers(report);

  const [result = {}] = studentsByScoreBracket({
    userIds,
    reportByUsers,
    itemId,
  });

  return result;
};

export const findReportByAssignmentIdSectionId = (
  reports,
  assignmentId,
  sectionId,
) => {
  const foundReport = reports.find(({ id: reportId }) => {
    // this is the learnosity report id, we set it in `resolve_assignments_results` in `service-learnosity`
    const [reportAssignmentId, reportSectionId] = reportId.split('_').splice(1); // no-unused-vars
    return (
      parseInt(reportAssignmentId, 10) === parseInt(assignmentId, 10) &&
      parseInt(reportSectionId, 10) === parseInt(sectionId, 10)
    );
  });
  return foundReport || {};
};

export const stacksToMessage = (stacks, isLocked) =>
  `${isLocked && 'Locked'}. ${stacks.reduce(
    (a, b) => `${a}${b.label}: ${b.value}, `,
    '',
  )}`;

/**
 * Gets the student result from a Learnosity report.
 * @param {object} learnosityReport - The Learnosity report.
 * @returns {object} Student score object with two keys: student actual points and maximum points available.
 * */
export const getStudentScore = (
  learnosityReport,
  learnosityReportsData,
  initId,
) => {
  if (Object.keys(learnosityReport).length) {
    const activityId = Object.keys(learnosityReport);

    if (activityId) {
      return getPerformanceStacks(
        learnosityReport,
        learnosityReportsData,
        initId,
      );
    }
  }

  return null;
};

export const getPerformanceStacks = (
  reportData,
  learnosityReportsData,
  initId,
) => {
  if (learnosityReportsData && learnosityReportsData.length) {
    let reportByStudents = {};
    const activityId = reportData && Object.keys(reportData)[0];

    if (activityId) {
      reportByStudents = reportData[activityId].users;
    }

    const [result] = studentsByScoreBracket({
      userIds: [initId],
      reportByUsers: reportByStudents,
    });

    return result;
  }
  return undefined;
};
